import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { cx } from './cx.js';
import { Icon } from './Icon.tsx';
import { toast as toastTokens } from '../tokens.js';

/**
 * Transient feedback, with an undo where one exists.
 *
 * An undo toast lasts longer than a plain one and is keyboard-focusable,
 * because an undo nobody can reach is decoration. The duration is a token, not
 * a number chosen per call site.
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  action?: ToastAction;
}

interface ToastContextValue {
  show: (toast: Omit<ToastMessage, 'id'>) => string;
  dismiss: (id: string) => void;
  messages: ToastMessage[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setMessages((current) => current.filter((m) => m.id !== id));
  }, []);

  const show = useCallback(
    (toast: Omit<ToastMessage, 'id'>) => {
      counter.current += 1;
      const id = `toast-${String(counter.current)}`;
      setMessages((current) => [...current, { ...toast, id }]);
      // An undo has to survive long enough to be clicked; everything else goes
      // sooner so it does not become furniture.
      const ms = toast.action ? toastTokens.undoMs : toastTokens.standardMs;
      setTimeout(() => {
        dismiss(id);
      }, ms);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ show, dismiss, messages }), [show, dismiss, messages]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost messages={messages} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast was called outside a ToastProvider. The app shell provides one.');
  }
  return context;
}

const TONE_ICON: Record<ToastTone, string> = {
  success: 'CheckCircle',
  error: 'Warning',
  info: 'Info',
};

const TONE_TEXT: Record<ToastTone, string> = {
  success: 'text-success',
  error: 'text-danger',
  info: 'text-info',
};

export function ToastHost({
  messages,
  onDismiss,
}: {
  messages: ToastMessage[];
  onDismiss: (id: string) => void;
}): JSX.Element {
  return (
    <div
      data-component="Toast"
      // Polite rather than assertive: a toast interrupting a screen reader
      // mid-sentence is worse than a toast heard a moment later.
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 end-4 z-50 flex max-w-popover flex-col gap-2"
    >
      {messages.map((message) => (
        <div
          key={message.id}
          // The tone is what the message means - worked, did not work, needs
          // reading. It was only ever expressed as an icon colour, which no
          // test and no screen reader can read.
          data-toast-tone={message.tone}
          className="pointer-events-auto flex items-start gap-3 rounded-card bg-surface p-3 shadow-popover"
        >
          <span className={cx('mt-px shrink-0', TONE_TEXT[message.tone])}>
            <Icon name={TONE_ICON[message.tone]} size="inline" decorative />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-dense font-medium text-ink">{message.title}</p>
            {message.body ? <p className="text-meta text-ink-meta">{message.body}</p> : null}
            {message.action ? (
              <button
                type="button"
                onClick={() => {
                  message.action?.onClick();
                  onDismiss(message.id);
                }}
                className="self-start text-dense font-medium text-bronze-text underline decoration-bronze-rule underline-offset-4"
              >
                {message.action.label}
              </button>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => onDismiss(message.id)}
            className="ms-auto shrink-0 text-ink-400 hover:text-ink"
          >
            <Icon name="X" size="inline" decorative />
          </button>
        </div>
      ))}
    </div>
  );
}
